import type { AgentModeKey, LlmRoutePurposeAny } from '@developerz/domain';
import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { KeyRow } from '../lib/keys-types';
import type { RouteWire } from '../lib/llmRoutes';
import type { RepoRouteSaves } from '../lib/repo-route-save';
import { AgentRepoOverrideAdd } from './AgentRepoOverrideAdd';
import { AgentRepoOverrideRow } from './AgentRepoOverrideRow';

// The Advanced drawer's Per-repo section (#5211 AC3, extended #5436) — every
// repo that already carries a route override for this purpose (the SAME
// population the card's repo chip counts, `repoOverrideCount`): its route
// pin, editable here through the SAME `ModelPicker` the card uses (model +
// provider) AND the SAME `AgentRouteAdvancedFields` the org purpose mounts
// (thinking depth, a specific key, a provider limit — #5436 gave these a
// repo-scope home; they no longer stay ORG-scope only), and, on a mode-backed
// agent, that repo's mode override rendered with NO enabled control (AC3's
// "read-only" half — edited from the repo's own Settings tab, never here).
//
// #5436: this section is mounted more than once now — once for the card's own
// purpose, and once per Coder task-tag purpose (`AgentAdvancedDrawer`'s
// "Fine-tune by task tag" section) — so its heading and its `aria-label` are
// the caller's `title`/`ariaLabel`, not a fixed string; two mounts in one
// drawer must not share an accessible name.
//
// The repo SET is not a second discovery call: `spec-gate` note 2 left it
// unspecified, and the cheapest honest answer is the repos that already have a
// route override — exactly what the repo chip already counts, so a repo this
// section lists is a repo the card already told the caller has one.
//
// ROUND 3 (#5381): a repo pin refusal renders through `ModelSaveNotice` now,
// not `RouteRefusalNotice` — `repo-route-save.ts` answers a `PendingRefusal`
// (the org picker's own shape), so a repo pin gets the SAME cures ("Apply mine
// anyway", "Apply and reset thinking depth"), never a lesser notice just
// because the row lives at repo scope.
//
// The rows live in `AgentRepoOverrideRow`, the add slot in `AgentRepoOverrideAdd`.
//
// COMPACT (the Default card, #5626): the same data on a quieter surface — a one-line
// summary per row that opens in place (which rows are open is this section's state, keyed
// by repo, so a save's refetch cannot close the row being edited), an "Add override" button in front of the add
// slot, and "No overrides yet" instead of an absent section, so the card says what it
// has rather than leaving a gap. The drawer's own mounts stay full.

export type AgentRepoOverridesSectionProps = {
  readonly agentName: string;
  readonly purpose: LlmRoutePurposeAny;
  /** This purpose's repo-scope rows only (`scope === 'repo'`). */
  readonly repoRoutes: readonly RouteWire[];
  /** repo id → full_name, for the section heading AND the add slot's
   * candidates. A repo not in the map (a stale route for a repo the account no
   * longer holds) falls back to its id. */
  readonly repoNames: ReadonlyMap<string, string>;
  readonly keys: { readonly data: readonly KeyRow[] | undefined; readonly isError: boolean };
  readonly canEdit: boolean;
  readonly saves: RepoRouteSaves;
  /** Present on a mode-backed agent — renders that repo's read-only mode row too. */
  readonly modeKey: AgentModeKey | undefined;
  readonly canEditModes: boolean;
  readonly token: string | null;
  /** The `<h3>` heading text — the caller's own, so a second mount for a
   * Coder task-tag purpose (#5436) reads distinctly from the card's own
   * purpose section. Already translated. */
  readonly title: string;
  /** The `<section>`'s accessible name. Already translated; may equal
   * {@link title} verbatim or differ, the caller's call. */
  readonly ariaLabel: string;
  /** `AgentKeyLabelField` requires it (a rename may move this purpose's own
   * pin, at whichever repo it is pinned to) — wired to `Agents.tsx`'s single
   * `onKeyRenamed`. */
  readonly onKeyRenamed: () => void;
  /** The Default card's quieter rendering (#5626); the drawer's mounts leave it off. */
  readonly compact?: boolean;
};

export function AgentRepoOverridesSection(props: AgentRepoOverridesSectionProps): JSX.Element {
  /** Every enabled repo NOT already listed above, sorted by name. */
  const candidates = createMemo(() => {
    const listed = new Set(props.repoRoutes.map((r) => r.scope_ref));
    return [...props.repoNames]
      .filter(([id]) => !listed.has(id))
      .sort((a, b) => a[1].localeCompare(b[1]));
  });
  const compact = (): boolean => props.compact === true;
  // Which compact rows are open, by repo id. It lives HERE and not in the row: a write
  // ends in a refetch that returns the saved route as a NEW object, `<For>` maps by
  // identity and re-creates exactly that row, so row-local state would snap the editor
  // shut on every field the user changes. The repo id survives the re-creation.
  const [openRepos, setOpenRepos] = createSignal<ReadonlySet<string>>(new Set());
  const toggle = (repoId: string): void => {
    setOpenRepos((prev) => {
      const next = new Set(prev);
      if (!next.delete(repoId)) next.add(repoId);
      return next;
    });
  };

  return (
    <Show when={compact() || props.repoRoutes.length > 0 || candidates().length > 0}>
      <section class="agent-drawer__section" aria-label={props.ariaLabel}>
        <h3 class="agent-drawer__section-title">{props.title}</h3>
        <Show when={compact() && props.repoRoutes.length === 0}>
          <p class="agent-drawer__repo-empty">{t('agents.drawer.repo.empty')}</p>
        </Show>
        <ul class="agent-drawer__repo-list">
          <For each={props.repoRoutes}>
            {(route) => (
              <AgentRepoOverrideRow
                route={route}
                ctx={props}
                compact={compact()}
                open={openRepos().has(route.scope_ref)}
                onToggle={() => toggle(route.scope_ref)}
              />
            )}
          </For>
        </ul>
        <Show when={props.canEdit && candidates().length > 0}>
          <AgentRepoOverrideAdd ctx={props} candidates={candidates()} compact={compact()} />
        </Show>
      </section>
    </Show>
  );
}
